import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { Stack, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FaceOutlinedIcon from "@mui/icons-material/FaceOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  getApprovedMockup,
  getCustomerTabNotes,
  getDispatchOrder,
  trackingOrder,
  generateOrder,
} from "../../api";
import OrderInfo from "../../components/OrderInfo";
import ArtworkDetail from "./ArtWorkPopup";
import OrderNotes from "../../components/OrderNotes";
import Layout from "../../components/Layout";
import TrackIdPopup from "./TrackIdPop";
import OrderStatusForFullfilled from "../../components/utils/OrderStatusForFullfilled";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

function getTheCurrentUrl() {
  const url = window.location.pathname;
  const pageId = url.split("/")[2];
  return pageId === "1" ? 0 : parseInt(pageId) - 1;
}

const section = "orders"; // Change dynamically based on the section

const columns = [
  { id: "checkbox", label: "", showIn: ["orders", "products"] },
  { id: "orderId", label: "Order ID", showIn: ["orders"] },
  { id: "view", label: "View", showIn: ["orders", "products"] },
  { id: "artwork", label: "Artwork", showIn: ["orders"] },
  { id: "track", label: "Track", showIn: ["orders"] },
  { id: "status", label: "Status", showIn: ["orders"] },
  { id: "notes", label: "Notes", showIn: ["orders"] },
  { id: "date", label: "Date", showIn: ["orders", "products"] },
];

// Filter columns based on section
const filteredColumns = columns.filter((col) => col.showIn.includes(section));

// function EnhancedTableHead() {
//   return (
//     <TableHead>
//       <TableRow>
//         <TableCell> Select </TableCell>
//         <TableCell> Order </TableCell>
//         <TableCell>Order details</TableCell>
//         <TableCell>Approved mockup</TableCell>
//         <TableCell>Tracking Id</TableCell>
//         <TableCell>Status</TableCell>
//         <TableCell>Notes</TableCell>
//         <TableCell>Dispatch date</TableCell>
//       </TableRow>
//     </TableHead>
//   );
// }
const EnhancedTableHead = ({ columns }) => (
  <TableHead>
    <TableRow>
      {columns.map((column) => (
        <TableCell key={column.id} sx={{ whiteSpace: "nowrap", padding: "6px 8px" }}>{column.label}</TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const DateComponent = ({ date }) => {
  return (
    <Box
      width="60%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <div>{date}</div>
    </Box>
  );
};

export default function EnhancedTable() {
  const navigate = useNavigate();
  const currentPageId = getTheCurrentUrl();
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(currentPageId);
  const [openModel, setOpenModel] = useState(false);
  const [orders, setOrders] = useState([]);
  const [rowData, setRowData] = useState({});
  const [artData, setArtData] = useState([]);
  const [artLoading, setArtLoading] = useState(false);
  const [id, setId] = useState(null);
  const [artDetailModal, setArtDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [openNotes, setOpenNotes] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [orderbyformat, setOrderByFormat] = useState("DESC");
  const currentRowsPerPage = localStorage.getItem("fulfilledRowsPerPage");
  const [rowsPerPage, setRowsPerPage] = useState(
    currentRowsPerPage ? parseInt(currentRowsPerPage) : 10
  );
  const [searchvalue, setSearchValue] = useState("");
  localStorage.setItem("FulfilledPage", currentPageId + 1);

  const handleCloseModel = () => {
    setOpenModel(false);
    setRowData({});
  };

  const handleSelectAllClick = (event) => {
    const value = event.target.value;
    setSelected((prevSelected) =>
      prevSelected.includes(value)
        ? prevSelected.filter((s) => s !== value)
        : [...prevSelected, value]
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    navigate(`/fulfilled/${newPage + 1}`);
  };

  const handleOrderDetails = useCallback((e, id) => {
    e.stopPropagation();
    const cpageId = window.location.pathname.split("/")[2];
    window.open(`/fulfilled/${cpageId}/details?id=${id}&tab=0`, "_blank");
  }, []);

  const handleModelOpen = useCallback((e, row) => {
    e.stopPropagation();
    setRowData(row);
    setOpenModel(true);
  }, []);

  const handleArtWork = useCallback(async (e, row) => {
    e.stopPropagation();
    setId(row.id);
    if (row.id) {
      setArtLoading(true);
      try {
        const data = await getApprovedMockup(row.id);
        if (data.status === 200) {
          setArtData(data.approvedmockups);
        }
      } catch (error) {
        console.error(error);
      }
      setArtLoading(false);
      setArtDetailModal(true);
    }
  }, []);

  const handleCloseArtModel = useCallback(() => {
    setArtDetailModal(false);
    setArtData([]);
    setId(null);
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchvalue);
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value === "") {
      setSearch("");
    }
  };

  const handleNotesOpen = useCallback((e, id) => {
    e.stopPropagation();
    setId(id);
    const fetchNotes = async () => {
      if (id) {
        try {
          setNotesLoading(true);
          const data = await getCustomerTabNotes(id);
          setNotes(data.per_product_information);
          setNotesLoading(false);
          setOpenNotes(true);
        } catch (error) {
          setNotesLoading(false);
          console.log(error);
        }
      }
    };
    fetchNotes();
  }, []);

  const handleNotesClose = useCallback((e, row) => {
    setId(null);
    setOpenNotes(false);
  }, []);

  const handleAscDesc = () => {
    if (orderbyformat === "DESC") {
      setOrderByFormat("ASC");
    } else {
      setOrderByFormat("DESC");
    }
  };
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setPage(0);
    if (e.target.value === "10") {
      localStorage.removeItem("fulfilledRowsPerPage");
    } else {
      localStorage.setItem("fulfilledRowsPerPage", e.target.value);
    }
  };
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const data = await getDispatchOrder(
          page + 1,
          rowsPerPage,
          search,
          orderbyformat
        );
        setOrders(data.responseData.response);
        setCount(data.responseData.order_count.count);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    };
    fetchOrder();
  }, [page, search, rowsPerPage, orderbyformat]);

  const isSelected = (name) => selected.indexOf(name) !== -1;

  // Tracking id
  const [trackData, setTrackData] = useState([]);
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackModal, setTrackModal] = useState(false);

  const handleTrackOrder = useCallback((e, row) => {
    e.stopPropagation();
    setId(row.id);
    const getTrackdetails = async () => {
      if (row.id) {
        try {
          setTrackLoading(true);
          const data = await trackingOrder(row.id);
          if (data.status === 200) {
            setTrackData(data.orderstatus);
          }
          setTrackLoading(false);
          setTrackModal(true);
        } catch (error) {
          setTrackLoading(false);
          console.log(error);
        }
      }
    };
    getTrackdetails();
  }, []);

  const handleCloseTrackModel = useCallback(() => {
    setTrackModal(false);
    setTrackData([]);
    setId(null);
  }, []);

  const handleGenerateAusPostOrder = async () => {
    const auspostresponse = await generateOrder(selected);
    if (auspostresponse.success) {
      alert("Order Created successfully");
    }
    // console.log(selected);
  };
  const handleClick = useCallback((event, name) => {
    console.log("table clicked");
  }, []);
  return (
    <Layout>
      <Box bgcolor="#f0f0f1" minHeight={`calc(100vh - 67px)`} px={3} py={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            sx={{
              marginRight: 2,
              fontWeight: 600, // Make it bold
              fontSize: "1.2rem", // Increase font size
              color: "#333", // Dark gray for better readability
              textTransform: "uppercase", // Uppercase for emphasis
              letterSpacing: "0.5px", // Slight letter spacing
            }}
          >
            Fulfilled Orders
          </Typography>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#1976d2", // Blue color
              color: "#fff",
              "&:hover": {
                backgroundColor: "#155a9a", // Darker blue on hover
              },
              padding: "8px 10px",
            }}
            size="small"
            onClick={handleAscDesc}
          >
            {orderbyformat === "DESC"
              ? "Show in DESC Order"
              : "Show in ASC Order"}
          </Button>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#388e3c", // Green color
              color: "#fff",
              "&:hover": {
                backgroundColor: "#2e7d32", // Darker green on hover
              },
              padding: "8px 10px",
            }}
            size="small"
            onClick={handleGenerateAusPostOrder}
          >
            Generate AusPost Order
          </Button>
        </Box>

        <Stack direction="row" justifyContent="flex-end" mt={2} flexWrap="wrap">
          {/* <TextField
            id="outlined-start-adornment"
            sx={{
              m: 1,
              width: "30ch",
              fontSize: "14px",
              backgroundColor: "#fff",
              height: "100%",
              marginBottom: "6px",
            }}
            size="small"
            placeholder="search"
            onChange={handleSearch}
            value={search}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          /> */}
          <form onSubmit={handleSearchSubmit}>
            <TextField
              id="outlined-start-adornment"
              sx={{
                m: 1,
                width: "30ch",
                backgroundColor: "#fff",
                fontSize: "14px",
              }}
              size="small"
              placeholder="search"
              onChange={handleSearch}
              value={searchvalue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon onClick={handleSearchSubmit} />
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Stack>
        <Paper>
          <TableContainer>
            {loading && <LinearProgress />}
            <Table
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
            >
              {/* <EnhancedTableHead

              // numSelected={selected.length}
              // onSelectAllClick={handleSelectAllClick}
              // rowCount={orders.length}
              /> */}
              <EnhancedTableHead columns={filteredColumns} />
              {/* <TableBody>
                {orders.map((row, index) => {
                  const isItemSelected = isSelected(row.name);
                  const labelId = `enhanced - table - checkbox - ${index} `;
                  let date =
                    row.dispatchdate === "N/A"
                      ? "N/A"
                      : moment(row.dispatchdate).format("ll");
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.name)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      selected={isItemSelected}
                      key={row.id}
                    >
                      <TableCell>
                        <Checkbox
                          value={row.id}
                          onChange={handleSelectAllClick}
                          // checked={selected.includes(row.id)}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        onClick={(e) => handleOrderDetails(e, row.id)}
                      >
                        {`#${row.id} ${row.billing.first_name} ${row.billing.last_name} `}
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleModelOpen(e, row)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleArtWork(e, row)}>
                          {artLoading && id === row.id ? (
                            <CircularProgress size={20} thickness={4} />
                          ) : (
                            <FaceOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleTrackOrder(e, row)}>
                          {trackLoading && id === row.id ? (
                            <CircularProgress size={20} thickness={4} />
                          ) : (
                            <LocalShippingIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <OrderStatusForFullfilled row={row} />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleNotesOpen(e, row.id)}>
                          {notesLoading && id === row.id ? (
                            <CircularProgress size={20} thickness={4} />
                          ) : (
                            <ChatOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <DateComponent date={date} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody> */}
              <TableBody>
                {orders.map((row, index) => (
                  <TableRow key={row.id}>
                    {filteredColumns.map((column) => (
                      <TableCell key={column.id} sx={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {column.id === "checkbox" && (
                          <Checkbox
                            size="small"
                            value={row.id}
                            onChange={handleSelectAllClick}
                          />
                        )}
                        {column.id === "orderId" && (
                          <span
                            style={{
                              color: "#2271b1",
                              cursor: "pointer",
                              textDecoration: "none",
                              fontWeight: "bold",
                            }}
                            onMouseOver={(e) =>
                              (e.target.style.textDecoration = "underline")
                            }
                            onMouseOut={(e) =>
                              (e.target.style.textDecoration = "none")
                            }
                            onClick={(e) => handleOrderDetails(e, row.id)}
                          >
                            {`#${row.id} ${row.billing.first_name} ${row.billing.last_name}`}
                          </span>
                        )}
                        {column.id === "view" && (
                          <IconButton size="small" onClick={(e) => handleModelOpen(e, row)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                        {column.id === "artwork" && (
                          <IconButton size="small" onClick={(e) => handleArtWork(e, row)}>
                            {artLoading && id === row.id ? (
                              <CircularProgress size={20} thickness={4} />
                            ) : (
                              <FaceOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        {column.id === "track" && (
                          <IconButton size="small" onClick={(e) => handleTrackOrder(e, row)}>
                            {trackLoading && id === row.id ? (
                              <CircularProgress size={20} thickness={4} />
                            ) : (
                              <LocalShippingIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        {column.id === "status" && (
                          <OrderStatusForFullfilled row={row} />
                        )}
                        {column.id === "notes" && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleNotesOpen(e, row.id)}
                          >
                            {notesLoading && id === row.id ? (
                              <CircularProgress size={20} thickness={4} />
                            ) : (
                              <ChatOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        )}
                        {column.id === "date" && (
                          <DateComponent
                            date={
                              row.dispatchdate === "N/A"
                                ? "N/A"
                                : moment(row.dispatchdate).format("ll")
                            }
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && <LinearProgress />}
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <OrderInfo
          handleClose={handleCloseModel}
          openModel={openModel}
          setOpenModel={setOpenModel}
          {...rowData}
        />
        <ArtworkDetail
          id={id}
          handleClose={handleCloseArtModel}
          openModel={artDetailModal}
          setOpenModel={setOpenModel}
          artData={artData}
        />
        <TrackIdPopup
          id={id}
          handleClose={handleCloseTrackModel}
          openModel={trackModal}
          setOpenModel={setOpenModel}
          trackData={trackData}
        />
        <OrderNotes
          handleClose={handleNotesClose}
          handleOpen={handleNotesOpen}
          notes={notes}
          open={openNotes}
          id={id}
        />
      </Box>
    </Layout>
  );
}
